import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { ADDRESS_BOOK } from "../../../api-calls/api-urls";
import { authApi, authImageApi } from "../../../api-calls/user-requests";

// Rows per request. The backend accepts up to 100, but each short Maps link
// is a network lookup, so smaller chunks keep every request well clear of
// the load balancer's 60s timeout.
const CHUNK_SIZE = 50;
const SKIP = "";

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const TEMPLATE_FILE_NAME = "Customer Import Template.xlsx";

// Saves a blob under an exact name. The name is set here rather than taken
// from the response, so it keeps its spaces in every browser.
function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadIssuesCsv(issues, fileName) {
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const lines = [
    ["Row", "Customer", "Result", "Reason"].map(escape).join(","),
    ...issues.map((issue) =>
      [issue.row_number, issue.customer_name, issue.kind, issue.reason]
        .map(escape)
        .join(","),
    ),
  ];
  const blob = new Blob([`﻿${lines.join("\r\n")}`], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(fileName || "customers").replace(/\.[^.]+$/, "")}-import-issues.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Bulk-adds customers from an Excel / CSV file: upload → map the file's
 * columns onto our fields → import in chunks → summary.
 *
 * @param {boolean} open
 * @param {string} companyId
 * @param {(createdCount: number) => void} onClose
 */
function BulkImportDialog({ open, companyId, onClose }) {
  const inputRef = useRef(null);
  const [step, setStep] = useState("pick"); // pick | map | importing | done
  const [parsing, setParsing] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState({ created: [], skipped: [], failed: [] });

  const reset = () => {
    setStep("pick");
    setParsing(false);
    setError("");
    setParsed(null);
    setMapping([]);
    setProgress({ done: 0, total: 0 });
    setResult({ created: [], skipped: [], failed: [] });
  };

  const handleClose = () => {
    if (step === "importing") return;
    const created = result.created.length;
    reset();
    onClose(created);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setError("Please choose an Excel (.xlsx, .xls) or CSV file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File is too large. The limit is 5 MB.");
      return;
    }
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await authImageApi.post(
        `${ADDRESS_BOOK}/${companyId}/import/parse`,
        formData,
      );
      setParsed(data);
      setMapping(data.suggestedMapping.map((key) => key || SKIP));
      setStep("map");
    } catch (err) {
      setError(errorMessage(err, "Could not read this file."));
    } finally {
      setParsing(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = async () => {
    setError("");
    setDownloadingTemplate(true);
    try {
      const { data } = await authApi.get(`${ADDRESS_BOOK}/import/template`, {
        responseType: "blob",
      });
      saveBlob(data, TEMPLATE_FILE_NAME);
    } catch {
      // With a blob response the server's JSON message isn't readable here,
      // and axios's own ("Request failed with status code 500") means
      // nothing to the user.
      setError("Couldn't download the template. Please check your connection and try again.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Picking a field that another column already holds moves it here, rather
  // than leaving two columns claiming the same field.
  const handleMappingChange = (col, key) => {
    setMapping((prev) =>
      prev.map((current, i) => {
        if (i === col) return key;
        if (key !== SKIP && current === key) return SKIP;
        return current;
      }),
    );
  };

  const missingRequired = (parsed?.fields || []).filter(
    (field) => field.required && !mapping.includes(field.key),
  );

  const handleImport = async () => {
    const records = parsed.rows.map((row) => {
      const record = { row_number: row.row_number };
      mapping.forEach((key, col) => {
        if (key !== SKIP) record[key] = row.values[col];
      });
      return record;
    });

    const totals = { created: [], skipped: [], failed: [] };
    setProgress({ done: 0, total: records.length });
    setStep("importing");

    // Sequential on purpose: each chunk's duplicate check has to see the
    // customers the previous chunk created.
    for (let start = 0; start < records.length; start += CHUNK_SIZE) {
      const chunk = records.slice(start, start + CHUNK_SIZE);
      try {
        const { data } = await authApi.post(
          `${ADDRESS_BOOK}/${companyId}/import`,
          { rows: chunk },
        );
        totals.created.push(...data.created);
        totals.skipped.push(...data.skipped);
        totals.failed.push(...data.failed);
      } catch (err) {
        const reason = errorMessage(err, "Server error");
        totals.failed.push(
          ...chunk.map((row) => ({
            row_number: row.row_number,
            customer_name: row.customer_name,
            reason,
          })),
        );
      }
      setProgress({ done: Math.min(start + CHUNK_SIZE, records.length), total: records.length });
    }

    setResult(totals);
    setStep("done");
  };

  const issues = [
    ...result.skipped.map((row) => ({ ...row, kind: "Skipped" })),
    ...result.failed.map((row) => ({ ...row, kind: "Failed" })),
  ].sort((a, b) => a.row_number - b.row_number);

  const sampleValues = (col) =>
    parsed.rows
      .map((row) => row.values[col])
      .filter((value) => value !== "")
      .slice(0, 3);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={step === "pick" ? "sm" : "md"}
      disableEscapeKeyDown={step === "importing"}
    >
      <DialogTitle sx={{ fontWeight: 700, pr: 6, position: "relative" }}>
        Bulk Upload Customers
        {step !== "importing" && (
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close"
          >
            {/* Colour on the svg itself: a global rule in the header's CSS
                module (`… p, svg { color: white }`) whitens every svg that
                doesn't set its own, which vanishes on a white dialog. */}
            <CloseIcon sx={{ color: "#6b778c" }} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {step === "pick" && (
          <>
            <Box
              onClick={() => !parsing && inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              sx={{
                border: "2px dashed",
                borderColor: dragOver ? "#227dc3" : "#d0d7e2",
                backgroundColor: dragOver ? "#eef5ff" : "#f8fafc",
                borderRadius: "12px",
                p: 5,
                textAlign: "center",
                cursor: parsing ? "default" : "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {parsing ? (
                <>
                  <CircularProgress size={36} />
                  <Typography mt={2}>Reading your file…</Typography>
                </>
              ) : (
                <>
                  <UploadFileIcon sx={{ fontSize: 44, color: "#227dc3" }} />
                  <Typography fontWeight={600} mt={1}>
                    Drop your Excel file here, or click to choose
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    .xlsx, .xls or .csv &middot; up to 2,000 customers &middot; max 5 MB
                  </Typography>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" mt={2}>
              Any column layout works — you'll match your columns to ours in
              the next step. Each customer needs a <b>name</b> and a{" "}
              <b>Google Maps link</b> to their location.
            </Typography>
            <Box
              mt={2}
              p={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
              sx={{ border: "1px solid #e3e8ef", borderRadius: "10px" }}
            >
              <Typography variant="body2">
                Starting from scratch? Download our template, fill in one
                customer per row, then upload it here.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate || parsing}
                startIcon={
                  downloadingTemplate ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                sx={{ textTransform: "none", flexShrink: 0 }}
              >
                Download Template
              </Button>
            </Box>
          </>
        )}

        {step === "map" && parsed && (
          <>
            <Typography mb={0.5}>
              Found <b>{parsed.totalRows}</b>{" "}
              {parsed.totalRows === 1 ? "row" : "rows"} in{" "}
              <b>{parsed.fileName}</b>
              {parsed.sheetName ? ` (sheet "${parsed.sheetName}")` : ""}.
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Check which of our fields each column should go into. Columns set
              to "Don't import" are ignored.
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              {parsed.fields
                .filter((field) => field.required)
                .map((field) => {
                  const ok = mapping.includes(field.key);
                  return (
                    <Chip
                      key={field.key}
                      size="small"
                      color={ok ? "success" : "error"}
                      variant={ok ? "filled" : "outlined"}
                      icon={ok ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                      label={`${field.label} ${ok ? "mapped" : "required"}`}
                    />
                  );
                })}
            </Box>
            <TableContainer
              sx={{ border: "1px solid #e8edf2", borderRadius: "12px", maxHeight: 420 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: "#f0f6ff" }}>
                      Column in your file
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: "#f0f6ff" }}>
                      Sample values
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: "#f0f6ff", width: 220 }}>
                      Import as
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.headers.map((header, col) => (
                    <TableRow key={`${header}-${col}`}>
                      <TableCell sx={{ fontWeight: 500 }}>{header}</TableCell>
                      <TableCell sx={{ color: "#6b778c", maxWidth: 280 }}>
                        {sampleValues(col).map((value, i) => (
                          <Typography
                            key={i}
                            variant="body2"
                            noWrap
                            title={value}
                            sx={{ color: "inherit" }}
                          >
                            {value}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          fullWidth
                          displayEmpty
                          value={mapping[col] ?? SKIP}
                          onChange={(e) => handleMappingChange(col, e.target.value)}
                          sx={{
                            backgroundColor: mapping[col] ? "#eef5ff" : "#fff",
                          }}
                        >
                          <MenuItem value={SKIP}>
                            <em>Don't import</em>
                          </MenuItem>
                          {parsed.fields.map((field) => (
                            <MenuItem key={field.key} value={field.key}>
                              {field.label}
                              {field.required ? " *" : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {step === "importing" && (
          <Box py={4} textAlign="center">
            <Typography fontWeight={600} mb={2}>
              Importing customers… {progress.done} of {progress.total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Short Maps links take a moment to look up. Please keep this
              window open.
            </Typography>
          </Box>
        )}

        {step === "done" && (
          <>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              {[
                { label: "Added", value: result.created.length, color: "#2e7d32" },
                { label: "Already existed", value: result.skipped.length, color: "#ed6c02" },
                { label: "Failed", value: result.failed.length, color: "#d32f2f" },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  flex="1 1 140px"
                  sx={{ border: "1px solid #e8edf2", borderRadius: "12px", p: 2 }}
                >
                  <Typography fontSize={28} fontWeight={700} color={stat.color}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {issues.length > 0 ? (
              <>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography fontWeight={600}>Rows not added</Typography>
                  <Button
                    size="small"
                    onClick={() => downloadIssuesCsv(issues, parsed?.fileName)}
                    sx={{ textTransform: "none" }}
                  >
                    Download as CSV
                  </Button>
                </Box>
                <TableContainer
                  sx={{ border: "1px solid #e8edf2", borderRadius: "12px", maxHeight: 320 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 70 }}>Row</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issues.map((issue) => (
                        <TableRow key={`${issue.kind}-${issue.row_number}`}>
                          <TableCell>{issue.row_number}</TableCell>
                          <TableCell>{issue.customer_name || "—"}</TableCell>
                          <TableCell
                            sx={{ color: issue.kind === "Failed" ? "#d32f2f" : "#ed6c02" }}
                          >
                            {issue.reason}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Alert severity="success">Every row was added.</Alert>
            )}
          </>
        )}
      </DialogContent>

      {step === "map" && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={reset} sx={{ textTransform: "none" }}>
            Choose another file
          </Button>
          <Box flex={1} />
          <Button
            variant="contained"
            disabled={missingRequired.length > 0}
            onClick={handleImport}
            sx={{ textTransform: "none" }}
          >
            Import {parsed?.totalRows} {parsed?.totalRows === 1 ? "customer" : "customers"}
          </Button>
        </DialogActions>
      )}
      {step === "done" && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={handleClose} sx={{ textTransform: "none" }}>
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default BulkImportDialog;
